/**
 * Left-edge drag resize for a box docked on the right — shared by the artifact panel (use-panel-resize.ts) and
 * the Kelly dock. Pointer-capture + rAF: the live drag writes the element's width imperatively (no per-frame
 * React render, no per-pixel Emotion class injection) and React state is committed only on pointer-up. Per the
 * repo's render rules nothing reads layout during render — the drag origin lives in refs.
 *
 * The clamp is captured ONCE per gesture, at pointer-down, so a reflow mid-drag cannot move the bounds under the
 * pointer; between gestures the CSS clamp keeps the painted width honest for free.
 */

import { useCallback, useEffect, useRef } from "react"
import type { PointerEvent as ReactPointerEvent, RefObject } from "react"

export type WidthClamp = (candidate: number) => number

function releaseCapture(event: ReactPointerEvent<HTMLElement>) {
  if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
}

export interface ResizeHandleProps {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void
  onLostPointerCapture: (event: ReactPointerEvent<HTMLElement>) => void
}

export interface UseEdgeResizeOptions {
  width: number
  onWidthChange: (width: number) => void
  /** Measured once, at pointer-down; returning null abandons the gesture (nothing to clamp against yet). */
  captureClamp: () => WidthClamp | null
  /**
   * The value React reconciles this element's `style.width` against, for a given committed width. Written back
   * on settle — see `settleDrag`. Must stay stable across renders: `settleDrag` is a host's mid-gesture escape
   * hatch and is bound into layout effects.
   */
  widthStyle: (width: number) => string
  /** Runs inside the drag's rAF — for anything that has to track the drag frame-for-frame (the dock's reservation). */
  onDragFrame?: (width: number) => void
}

export interface UseEdgeResizeResult {
  elementRef: RefObject<HTMLDivElement | null>
  resizeHandleProps: ResizeHandleProps
  /** End any gesture in flight and commit the width already painted. Idempotent; for a host that closes mid-drag. */
  settleDrag: () => void
}

export function useEdgeResize({ width, onWidthChange, captureClamp, widthStyle, onDragFrame }: UseEdgeResizeOptions): UseEdgeResizeResult {
  const elementRef = useRef<HTMLDivElement>(null)
  const dragOriginRef = useRef<{ pointerId: number, startX: number, startWidth: number, clamp: WidthClamp } | null>(null)
  const liveWidthRef = useRef(width)
  // Whether this gesture ever moved. Not "did the width change": a drag out and back past the ceiling paints raw
  // px at an unchanged width and still has to settle (see below).
  const hasMovedRef = useRef(false)
  const rafRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    liveWidthRef.current = width
  }, [width])
  useEffect(() => () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current) }, [])

  /**
   * Every exit from a gesture funnels here: it commits the width the element is ALREADY painting. Leaving a
   * gesture uncommitted desyncs the imperative inline width from React's own `style` prop, and because that
   * prop's value never changed React will not rewrite it — so anything else keyed off the committed width
   * (the dock's page reservation) silently paints against a different number for the rest of the session.
   */
  const settleDrag = useCallback(() => {
    if (dragOriginRef.current == null) return
    dragOriginRef.current = null
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = undefined
    }
    // A press and release that never moved has painted nothing and chosen nothing. Committing it anyway is not a
    // harmless no-op write: the commit is a SIGNAL, and the dock reads it as the user taking ownership of the
    // width — dropping, permanently, the width it owes an open artifact when that artifact closes.
    if (!hasMovedRef.current) return
    hasMovedRef.current = false
    const settledWidth = liveWidthRef.current
    onWidthChange(settledWidth)
    // Hand the element back to the CSS clamp. The commit alone is not enough for the same reason as above: a
    // drag that settles on the width it started from leaves React reconciling an unchanged prop, so the raw px
    // the last frame painted outlives the gesture — and diverges the moment the container narrows past the ceiling.
    if (elementRef.current != null) elementRef.current.style.width = widthStyle(settledWidth)
  }, [onWidthChange, widthStyle])

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    // A second pointer landing on the handle would otherwise overwrite the origin and orphan the first — whose
    // pointerup then fails the id check, so it never commits and never releases capture. touchAction:none makes
    // a two-finger landing plausible now that this handle is app-level furniture.
    if (dragOriginRef.current != null) return
    const clamp = captureClamp()
    if (clamp == null) return
    hasMovedRef.current = false
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    dragOriginRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      // Start from what the CSS clamp is actually painting, not the stored prop, or the first move jumps.
      startWidth: clamp(liveWidthRef.current),
      clamp,
    }
  }, [captureClamp])

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const origin = dragOriginRef.current
    if (origin == null || event.pointerId !== origin.pointerId) return
    // A gesture can end without pointerup (right-click, OS pointer steal, a native drag): no button held, no drag.
    // Disarming is what stops a later hover resizing, but it still has to COMMIT — see settleDrag.
    if (event.buttons === 0) {
      releaseCapture(event)
      settleDrag()
      return
    }
    // Docked on the RIGHT — dragging the handle left (smaller clientX) widens the box.
    const next = origin.clamp(origin.startWidth + (origin.startX - event.clientX))
    hasMovedRef.current = true
    liveWidthRef.current = next
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      if (elementRef.current != null) elementRef.current.style.width = `${next}px`
      onDragFrame?.(next)
    })
  }, [onDragFrame, settleDrag])

  const endDrag = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const origin = dragOriginRef.current
    if (origin == null || event.pointerId !== origin.pointerId) return
    releaseCapture(event)
    settleDrag()
  }, [settleDrag])

  return {
    elementRef,
    settleDrag,
    // onLostPointerCapture: capture can be revoked without pointerup (element removed, OS steal) — releasing the
    // origin cannot be one handler's sole responsibility. It is idempotent after endDrag cleared the origin.
    resizeHandleProps: { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag, onLostPointerCapture: endDrag },
  }
}
